import React, { useState } from 'react';
import { Auth } from 'aws-amplify';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';

const CLONE_URL = 'https://api.gogamify.com/signup/clone';

const getToken = async () => {
  const session = await Auth.currentSession();
  return session.getIdToken().getJwtToken();
};

const STEPS = ['Source Org', 'New Org Details', 'Clone in Progress'];

export default function OrgCloner({ orgsList = [] }) {
  const [activeStep, setActiveStep] = useState(0);
  const [sourceOrgId, setSourceOrgId] = useState('');
  const [orgName, setOrgName] = useState('');
  const [email, setEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [cloneStatus, setCloneStatus] = useState(null); // null | 'loading' | 'success' | 'error'
  const [cloneError, setCloneError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const queryClient = useQueryClient();

  const startClone = async () => {
    setConfirmOpen(false);
    setActiveStep(2);
    setCloneStatus('loading');
    setCloneError(null);
    try {
      const token = await getToken();
      const payload = {
        sourceOrgId: sourceOrgId.trim(),
        orgName: orgName.trim(),
        email: email.trim(),
      };
      if (contactPhone.trim()) {
        payload.contactPhone = contactPhone.trim();
      }
      const res = await fetch(CLONE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Request failed (${res.status})`);
      }
      setCloneStatus('success');
      queryClient.invalidateQueries({ queryKey: ['orgs'] });
    } catch (err) {
      setCloneStatus('error');
      setCloneError(friendlyError(err.message));
    }
  };

  const friendlyError = (msg) => {
    if (!msg) return 'Something went wrong. Please try again.';
    if (/already exists/i.test(msg)) return 'That org ID is already in use.';
    if (/not found/i.test(msg)) return 'The source org could not be found.';
    return msg;
  };

  const reset = () => {
    setActiveStep(0);
    setSourceOrgId('');
    setOrgName('');
    setEmail('');
    setContactPhone('');
    setCloneStatus(null);
    setCloneError(null);
  };

  const orgOptions = orgsList.map((org) => ({
    id: org.id,
    name: org.name || '',
  }));

  const canProceedToStep2 = sourceOrgId.trim().length > 0;
  const canStartClone = orgName.trim().length > 0 && email.trim().length > 0;

  return (
    <Paper elevation={1} sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 2 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Org Cloning
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Create a new org by cloning an existing one. This is a one-way
        operation.
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step 1: Source Org */}
      {activeStep === 0 && (
        <Box>
          <Autocomplete
            freeSolo
            options={orgOptions}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.id
            }
            filterOptions={(options, { inputValue }) => {
              const term = inputValue.toLowerCase();
              return options.filter(
                (o) =>
                  o.id.toLowerCase().includes(term) ||
                  (o.name && o.name.toLowerCase().includes(term))
              );
            }}
            isOptionEqualToValue={(option, value) =>
              option.id === (typeof value === 'string' ? value : value.id)
            }
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <span style={{ fontWeight: 600 }}>{option.id}</span>
                {option.name && (
                  <span style={{ color: '#666', marginLeft: 8 }}>
                    {option.name}
                  </span>
                )}
              </li>
            )}
            inputValue={sourceOrgId}
            onInputChange={(_, val) => setSourceOrgId(val)}
            onChange={(_, val) => {
              if (val && typeof val === 'object') setSourceOrgId(val.id);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="Source Org ID"
                placeholder="Search or type an org ID"
                helperText="The existing org whose configuration will be copied"
              />
            )}
          />
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              disabled={!canProceedToStep2}
              onClick={() => setActiveStep(1)}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}

      {/* Step 2: New Org Details */}
      {activeStep === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            required
            label="Org Name"
            placeholder="Enter the new org name"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            helperText="The display name for the new org"
          />
          <TextField
            fullWidth
            required
            label="Email"
            type="email"
            placeholder="contact@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            helperText="Contact email for the new org"
          />
          <TextField
            fullWidth
            label="Contact Phone"
            placeholder="Optional"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            helperText="Optional contact phone number"
          />
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={() => setActiveStep(0)}>Back</Button>
            <Button
              variant="contained"
              disabled={!canStartClone}
              onClick={() => setConfirmOpen(true)}
            >
              Start Clone
            </Button>
          </Box>
        </Box>
      )}

      {/* Step 3: Progress */}
      {activeStep === 2 && (
        <Box sx={{ textAlign: 'center' }}>
          {cloneStatus === 'loading' && (
            <>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="h6">Cloning in progress…</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This may take a moment. Please wait.
              </Typography>
            </>
          )}
          {cloneStatus === 'success' && (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                Org <strong>{orgName}</strong> has been created successfully.
              </Alert>
              <Button variant="contained" onClick={reset}>
                Clone Another
              </Button>
            </>
          )}
          {cloneStatus === 'error' && (
            <>
              <Alert severity="error" sx={{ mb: 2 }}>
                {cloneError}
              </Alert>
              <Button variant="outlined" onClick={reset}>
                Start Over
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Clone</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will create a new org <strong>{orgName}</strong> by cloning{' '}
            <strong>{sourceOrgId}</strong>. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={startClone}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
